/**
 * bindGraftedDigits — the engine half of ADR-010 §2 ④ "Bind". ONE responsibility:
 * take the kernel's engine-neutral {@link VertexInfluence} list and make a three.js
 * `SkinnedMesh` actually deform by it. Everything here is `BufferAttribute` and
 * `Skeleton` surgery; no geometry decisions are taken (ADR-002 §6 — the weights were
 * decided in `kernel/hands/bind-weights`, where they are testable without a scene).
 *
 * WHY THIS EXISTS AT ALL. `materializeMissingBones` grows a `Bone` per grafted id so
 * retargeted finger tracks have somewhere to land, and they do land — the bones
 * rotate correctly. But a `SkinnedMesh` deforms through `skeleton.bones` plus the
 * geometry's `skinIndex`/`skinWeight`, and a `Bone` added to the scene GRAPH joins
 * NEITHER. That is the gap `app/src/hands/bind-gap-probe.test.ts` pins as two zeroes.
 * This module inverts both, and it must invert both: a bone in the skeleton with no
 * vertex weighted to it still moves nothing, and a weight toward a bone the skeleton
 * lacks is an out-of-range index that corrupts the whole mesh.
 *
 * THE INVERSES COME FROM THE RIG, NOT THE SCENE. `boneInverses[i]` must be the
 * inverse of bone i's world matrix AT BIND TIME. Reading it from `bone.matrixWorld`
 * would silently bake in whatever pose the scene happens to be holding — a clip left
 * applied would permanently skew every grafted finger. So each inverse is composed
 * from the rig descriptor's `worldBind`, which is pose-independent by construction
 * and is the very transform the graft authored the bone at (the same identity
 * `isolateHandRegion` relies on: IBM-derived bind origins ≡ the kernel's worldBind).
 *
 * THERE IS NO FIFTH SLOT. three.js skins FOUR influences per vertex, and on this
 * corpus all four are already spoken for (hand + forearm + twist supplements). So a
 * digit influence cannot be added — it must EVICT. The rule, stated rather than
 * discovered: existing influences are first scaled by `1 − digitShare` (the share the
 * kernel handed to grafted bones, which is <1 wherever the retained hand bone still
 * competes), the digit influences are merged in, the combined set is sorted by
 * weight, the strongest four survive and are renormalised to sum to 1. That is
 * LOSSY — a fifth influence is genuinely discarded — and the loss is bounded by the
 * weight of the smallest survivor. Partition of unity is restored by the
 * renormalisation and is asserted, because a renormalisation bug shows up as
 * geometry that shrinks toward the origin rather than as an error.
 *
 * Vertices absent from the influence list are NOT TOUCHED — not re-normalised, not
 * rewritten. That is what makes "nothing outside the hand moves" a property of the
 * code rather than a hope, and it is asserted from the caller's side.
 *
 * Fail-loud (LAWS): a mesh without skin attributes, a grafted bone with no scene
 * node or no rig entry, an influence naming an unknown bone, or a non-finite weight
 * all throw {@link HandBindError}. A vertex index outside the geometry is the same
 * class of bug and is caught with the rest, every issue collected.
 *
 * Depends on: three, @harryhausen/kernel (VertexInfluence, RigDescriptor). Depended
 * on by: adapter index, app/session/run-document.
 */
import {
  Bone,
  Matrix4,
  Quaternion,
  Skeleton,
  SkinnedMesh,
  Uint16BufferAttribute,
  Vector3,
  type Object3D,
} from 'three';
import type { RigDescriptor, VertexInfluence } from '@harryhausen/kernel';
import { AdapterError } from '../errors.js';
import { seatInfluences } from '../weights/seat-influences.js';

/** A cannot-bind condition — a producer/caller bug, fail-loud (ADR-010 §7). */
export class HandBindError extends AdapterError {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot bind grafted digits (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

export interface BindGraftedDigitsResult {
  /** Grafted bones added to `skeleton.bones` (they were only in the scene graph). */
  readonly bonesAdded: readonly string[];
  /** Vertices whose influences were rewritten. */
  readonly verticesReweighted: number;
  /** Influence slots that had to be evicted to make room — the lossy part, counted. */
  readonly influencesEvicted: number;
  /** Largest weight discarded by an eviction; the honest bound on what was lost. */
  readonly largestEviction: number;
  /** Worst |Σw − 1| over rewritten vertices — partition of unity, measured not assumed. */
  readonly worstWeightSumError: number;
  /** Vertices whose forearm-chain weight was re-owned by the hand (wrist boundary law). */
  readonly verticesWristReowned: number;
  /** Full four-slot seam vertices left AS PAINTED because the hand's moved share would not seat (don't move what you cannot seat) — counted, never silent. */
  readonly verticesWristUnseated: number;
}

/**
 * THE WRIST IS THE BOUNDARY AUTHORITY, NOT THE WEIGHTS (ADR-025) — closed at the
 * seat, its last open door. The generator paints forearm-twist weight across the
 * wrist into palm and thumb flesh, and the seat's `1 − digitShare` scaling
 * preserved that paint wherever the digits didn't claim (which, after the palm
 * retain fan, is exactly the palm). Measured on Sergio's field Document
 * (2026-08-20): `R_ForearmTwist02` DOMINATED 73 hand-region vertices — nearly as
 * many as the hand bone itself (82) — and the field read it as "crossover leak
 * from the wrist bones". Hand-region flesh distal of the confirmed wrist plane
 * hands its forearm-chain weight to the HAND bone; `factor` ramps 0→1 across a
 * one-voxel seam band at the cut so pronation still blends instead of creasing.
 */
export interface WristReown {
  /** The retained hand bone that receives the transferred weight. */
  readonly handBone: string;
  /** The severed forearm chain (forearm + its non-hand descendants) losing it. */
  readonly chainBoneNames: readonly string[];
  /**
   * The confirmed wrist cut plane in BIND-WORLD space, normal toward the hand.
   * The candidate set is computed HERE over the whole mesh, not from the
   * isolation region: the very vertices this law exists for are
   * twist-DOMINATED, so the dominant-weight region never contained them
   * (measured: Sergio R kept all 73 twist-dominated vertices after a
   * region-scoped pass, because none of them were in the region).
   */
  readonly plane: { readonly point: readonly [number, number, number]; readonly normal: readonly [number, number, number] };
  /** Only flesh within this distance of the plane point is the hand's (locality — the plane is infinite, the wrist is not). */
  readonly radiusWorld: number;
  /** Seam band width in world units; factor ramps 0→1 across it. */
  readonly bandWorld: number;
  /** Skirt riders and other flesh that must keep its weights untouched. */
  readonly excludeVertexIndices: ReadonlySet<number>;
}

/** Compose a rig bone's pose-independent bind matrix from its authored worldBind. */
function worldBindMatrix(rig: RigDescriptor, boneName: string): Matrix4 | null {
  const bone = rig.bones.find((b) => b.name === boneName);
  if (bone === undefined) return null;
  const { position: p, rotation: q, scale: s } = bone.worldBind;
  return new Matrix4().compose(
    new Vector3(p.x, p.y, p.z),
    new Quaternion(q.x, q.y, q.z, q.w),
    new Vector3(s.x, s.y, s.z),
  );
}

/**
 * Seat kernel-computed digit weights into a SkinnedMesh and extend its skeleton to
 * the grafted bones. See the module header for the contract; throws
 * {@link HandBindError} on a wiring bug.
 */
export function bindGraftedDigits(
  mesh: SkinnedMesh,
  scene: Object3D,
  rig: RigDescriptor,
  graftedBoneNames: readonly string[],
  influences: readonly VertexInfluence[],
  reown?: WristReown,
  /**
   * THE DONOR (ADR-038 J2, 2026-09-27): the claim's weights are SHARES of this
   * one bone's paint, and only it gives weight — a claim `s` on a vertex takes
   * `s × w_donor` from the donor and every other influence keeps its weight.
   * A grafted child of the donor that sits at rest then moves exactly as the
   * flesh did: the rest-follow is unchanged by construction. For a soft-tissue
   * leaf hung from the owner of its flesh (Amelie: the uniform `1 − share`
   * scaling left her breast's centre 11.7 mm off the leafless body under a
   * walk with the leaf at rest). Absent = the digits' uniform scaling.
   */
  donor?: string,
): BindGraftedDigitsResult {
  const issues: string[] = [];
  const position = mesh.geometry.getAttribute('position');
  let skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  if (!skinIndex || !skinWeight) {
    throw new HandBindError(['mesh is not skinned (no skinIndex/skinWeight) — there is nothing to rebind']);
  }
  const influencesPerVertex = skinIndex.itemSize;

  // --- resolve every grafted bone to BOTH a scene node and a rig entry ---
  const toAdd: { name: string; node: Bone; inverse: Matrix4 }[] = [];
  const existing = new Map(mesh.skeleton.bones.map((b, i) => [b.name, i] as const));
  for (const name of graftedBoneNames) {
    if (existing.has(name)) continue; // already skinned through — idempotent
    const node = scene.getObjectByName(name);
    if (node === undefined) {
      issues.push(`grafted bone "${name}" has no scene node (materializeMissingBones must run first)`);
      continue;
    }
    if (!(node as Bone).isBone) {
      issues.push(`scene node "${name}" is a ${node.type}, not a Bone — cannot skin through it`);
      continue;
    }
    const bind = worldBindMatrix(rig, name);
    if (bind === null) {
      issues.push(`grafted bone "${name}" is not in the rig descriptor — no worldBind to invert`);
      continue;
    }
    const det = bind.determinant();
    if (!Number.isFinite(det) || Math.abs(det) < 1e-12) {
      issues.push(`grafted bone "${name}" has a degenerate worldBind (det=${det}) — cannot invert`);
      continue;
    }
    toAdd.push({ name, node: node as Bone, inverse: bind.invert() });
  }
  if (issues.length > 0) throw new HandBindError(issues);

  // --- extend the skeleton: bones AND boneInverses together, or the bone deforms
  //     about the wrong origin. A fresh Skeleton rather than pushing in place, so
  //     boneMatrices/boneTexture are re-sized by three rather than left stale.
  const bones = [...mesh.skeleton.bones, ...toAdd.map((a) => a.node)];
  const boneInverses = [...mesh.skeleton.boneInverses, ...toAdd.map((a) => a.inverse)];
  const boneSlot = new Map(bones.map((b, i) => [b.name, i] as const));

  // --- validate influences BEFORE mutating anything ---
  const vertexCount = position ? position.count : skinIndex.count;
  const byVertex = new Map<number, { slot: number; weight: number }[]>();
  for (const inf of influences) {
    const slotIndex = boneSlot.get(inf.boneId);
    if (slotIndex === undefined) {
      issues.push(`influence names bone "${inf.boneId}", which is not in the extended skeleton`);
      continue;
    }
    if (!Number.isInteger(inf.vertexIndex) || inf.vertexIndex < 0 || inf.vertexIndex >= vertexCount) {
      issues.push(`influence vertex index ${inf.vertexIndex} is outside [0, ${vertexCount})`);
      continue;
    }
    if (!Number.isFinite(inf.weight) || inf.weight < 0) {
      issues.push(`influence for bone "${inf.boneId}" at vertex ${inf.vertexIndex} has weight ${inf.weight}`);
      continue;
    }
    const list = byVertex.get(inf.vertexIndex);
    if (list) list.push({ slot: slotIndex, weight: inf.weight });
    else byVertex.set(inf.vertexIndex, [{ slot: slotIndex, weight: inf.weight }]);
  }
  if (issues.length > 0) throw new HandBindError(issues);

  // A grafted bone index can exceed what a Uint8 skinIndex can address; widen the
  // attribute rather than wrapping around into a wrong (and plausible) bone.
  const maxSlot = bones.length - 1;
  const widen = maxSlot > 255 && (skinIndex.array as ArrayLike<number>) instanceof Uint8Array;
  if (widen) {
    const widened = new Uint16Array(skinIndex.count * influencesPerVertex);
    for (let i = 0; i < widened.length; i += 1) widened[i] = (skinIndex.array as ArrayLike<number>)[i]!;
    const attr = new Uint16BufferAttribute(widened, influencesPerVertex);
    mesh.geometry.setAttribute('skinIndex', attr);
    skinIndex = mesh.geometry.getAttribute('skinIndex');
  }

  // --- reweight, evicting the weakest (see the header: there is no fifth slot) ---
  let influencesEvicted = 0;
  let largestEviction = 0;
  let worstWeightSumError = 0;

  // --- the wrist re-own runs FIRST (see WristReown): it rewrites only retained
  //     slots, and the digit pass below rebuilds its view from the live
  //     attributes per vertex, so the two compose without coordination. ---
  let verticesWristReowned = 0;
  let verticesWristUnseated = 0;
  if (reown !== undefined) {
    const handSlot = boneSlot.get(reown.handBone);
    if (handSlot === undefined) {
      throw new HandBindError([`wrist re-own names hand bone "${reown.handBone}", which is not in the skeleton`]);
    }
    if (!(reown.bandWorld > 0) || !(reown.radiusWorld > 0)) {
      throw new HandBindError([`wrist re-own needs positive band/radius, got band=${reown.bandWorld} radius=${reown.radiusWorld}`]);
    }
    if (!position) {
      throw new HandBindError(['wrist re-own needs vertex positions — mesh has no position attribute']);
    }
    // Chain names absent from THIS skeleton are fine — not every rig has twists.
    const chainSlots = new Set<number>();
    for (const n of reown.chainBoneNames) {
      const s = boneSlot.get(n);
      if (s !== undefined) chainSlots.add(s);
    }
    const [px, py, pz] = reown.plane.point;
    const [rnx, rny, rnz] = reown.plane.normal;
    const nLen = Math.hypot(rnx, rny, rnz) || 1;
    const bindPos = new Vector3();
    for (let vertexIndex = 0; vertexIndex < vertexCount && chainSlots.size > 0; vertexIndex += 1) {
      if (reown.excludeVertexIndices.has(vertexIndex)) continue;
      // Bind-world position: bindMatrix · geometry position (the isolate idiom) —
      // pose-independent, the same space the plane was measured in.
      bindPos
        .set(position!.getX(vertexIndex), position!.getY(vertexIndex), position!.getZ(vertexIndex))
        .applyMatrix4(mesh.bindMatrix);
      const dx = bindPos.x - px;
      const dy = bindPos.y - py;
      const dz = bindPos.z - pz;
      const distal = (dx * rnx + dy * rny + dz * rnz) / nLen;
      if (!(distal > 0)) continue; // proximal of the wrist — the arm keeps its paint
      if (dx * dx + dy * dy + dz * dz > reown.radiusWorld * reown.radiusWorld) continue; // not this wrist's flesh
      const f = Math.min(1, distal / reown.bandWorld);
      const merged = new Map<number, number>();
      let moved = 0;
      for (let k = 0; k < influencesPerVertex; k += 1) {
        const w = skinWeight.getComponent(vertexIndex, k);
        if (w <= 0) continue;
        const slotIndex = skinIndex.getComponent(vertexIndex, k);
        if (chainSlots.has(slotIndex)) {
          const take = w * f;
          moved += take;
          const keep = w - take;
          if (keep > 0) merged.set(slotIndex, (merged.get(slotIndex) ?? 0) + keep);
        } else {
          merged.set(slotIndex, (merged.get(slotIndex) ?? 0) + w);
        }
      }
      if (moved <= 0) continue;
      merged.set(handSlot, (merged.get(handSlot) ?? 0) + moved);
      // Don't move what you cannot seat (seat-influences): at the seam band's
      // proximal edge the moved share is the smallest of five; the vertex
      // keeps its forearm paint until the share outranks the weakest.
      const { kept, evicted, unseatedReceivers } = seatInfluences(merged, new Set([handSlot]), influencesPerVertex);
      if (unseatedReceivers.length > 0) {
        verticesWristUnseated += 1;
        continue;
      }
      for (const [, w] of evicted) {
        influencesEvicted += 1;
        if (w > largestEviction) largestEviction = w;
      }
      const total = kept.reduce((s, [, w]) => s + w, 0);
      if (!(total > 0)) {
        throw new HandBindError([`vertex ${vertexIndex} would be left with zero total skin weight by the wrist re-own`]);
      }
      for (let k = 0; k < influencesPerVertex; k += 1) {
        const entry = kept[k];
        skinIndex.setComponent(vertexIndex, k, entry ? entry[0] : 0);
        skinWeight.setComponent(vertexIndex, k, entry ? entry[1] / total : 0);
      }
      let check = 0;
      for (let k = 0; k < influencesPerVertex; k += 1) check += skinWeight.getComponent(vertexIndex, k);
      worstWeightSumError = Math.max(worstWeightSumError, Math.abs(check - 1));
      verticesWristReowned += 1;
    }
  }

  const donorSlot = donor === undefined ? undefined : boneSlot.get(donor);
  if (donor !== undefined && donorSlot === undefined) throw new HandBindError([`donor bone "${donor}" is not in the skeleton`]);
  for (const [vertexIndex, digits] of byVertex) {
    const digitShare = Math.min(1, digits.reduce((s, d) => s + d.weight, 0));
    const merged = new Map<number, number>();
    if (donorSlot !== undefined) {
      // Only the donor gives: the claim is a share OF its weight here.
      let wDonor = 0;
      for (let k = 0; k < influencesPerVertex; k += 1) {
        const w = skinWeight.getComponent(vertexIndex, k);
        if (w <= 0) continue;
        const slotIndex = skinIndex.getComponent(vertexIndex, k);
        if (slotIndex === donorSlot) wDonor += w;
        else merged.set(slotIndex, (merged.get(slotIndex) ?? 0) + w);
      }
      if (wDonor * (1 - digitShare) > 0) merged.set(donorSlot, wDonor * (1 - digitShare));
      if (digitShare > 0) for (const d of digits) merged.set(d.slot, (merged.get(d.slot) ?? 0) + d.weight * wDonor);
    } else {
      // Existing influences yield exactly the share the digits claimed — no more.
      for (let k = 0; k < influencesPerVertex; k += 1) {
        const w = skinWeight.getComponent(vertexIndex, k) * (1 - digitShare);
        if (w <= 0) continue;
        const slotIndex = skinIndex.getComponent(vertexIndex, k);
        merged.set(slotIndex, (merged.get(slotIndex) ?? 0) + w);
      }
      for (const d of digits) merged.set(d.slot, (merged.get(d.slot) ?? 0) + d.weight);
    }
    for (const [slot, w] of merged) if (!(w > 0)) merged.delete(slot);

    // Strength order, deliberately NOT the reserved seat the single-receiver
    // doors use (seat-influences): here the claimants are MANY — a palm
    // vertex can be claimed by four or five digits at once — and seating
    // every claimant first evicted the HAND itself (measured on Ian's field
    // Document: the retained palm bone lost its seat at weight 0.94,
    // R_Hand dominance 283→12). The palm owner must outrank tiny digit
    // claims; the only order that protects it is strength.
    const sorted = [...merged.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0]);
    const kept = sorted.slice(0, influencesPerVertex);
    for (const [, w] of sorted.slice(influencesPerVertex)) {
      influencesEvicted += 1;
      if (w > largestEviction) largestEviction = w;
    }
    const total = kept.reduce((s, [, w]) => s + w, 0);
    if (!(total > 0)) {
      // Every influence cancelled — impossible unless the digits summed to 1 and
      // then all sorted out, but a zero-weight vertex renders as collapsed geometry,
      // so refuse rather than write it.
      throw new HandBindError([`vertex ${vertexIndex} would be left with zero total skin weight`]);
    }
    for (let k = 0; k < influencesPerVertex; k += 1) {
      const entry = kept[k];
      skinIndex.setComponent(vertexIndex, k, entry ? entry[0] : 0);
      skinWeight.setComponent(vertexIndex, k, entry ? entry[1] / total : 0);
    }
    let check = 0;
    for (let k = 0; k < influencesPerVertex; k += 1) check += skinWeight.getComponent(vertexIndex, k);
    worstWeightSumError = Math.max(worstWeightSumError, Math.abs(check - 1));
  }
  skinIndex.needsUpdate = true;
  skinWeight.needsUpdate = true;

  // --- rebind through the extended skeleton, KEEPING the original bindMatrix ---
  mesh.bind(new Skeleton(bones, boneInverses), mesh.bindMatrix.clone());

  return {
    bonesAdded: toAdd.map((a) => a.name),
    verticesReweighted: byVertex.size,
    influencesEvicted,
    largestEviction,
    worstWeightSumError,
    verticesWristReowned,
    verticesWristUnseated,
  };
}
